import React, { useState, useEffect } from 'react';
import { useMiniERP } from './hooks/useMiniERP';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faToggleOn, faToggleOff, faSearch, faUser } from '@fortawesome/free-solid-svg-icons';

const CODE_LENGTH = 14;

const MiniERPAuthorisedUser = () => {
  const {
    fetchAuthorisedUsers,
    createAuthorisedUser,
    updateAuthorisedUser,
    toggleAuthorisedUser
  } = useMiniERP();

  const { status, showSuccess, showError } = useStatusBanner();
  const [listStatus, setListStatus] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    authorisedUserCode: '',
    name: '',
    isActive: true
  });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await fetchAuthorisedUsers();
        setUsers(res?.data || []);
      } catch (err) {
        console.error("Failed to load authorised users", err);
      }
    };
    loadUsers();
  }, []);

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.authorisedUserCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingUser && formData.authorisedUserCode.length !== CODE_LENGTH) {
      showError(`User Code must be exactly ${CODE_LENGTH} characters (got ${formData.authorisedUserCode.length}).`);
      return;
    }
    if (!formData.name.trim()) {
      showError('Name is required.');
      return;
    }

    try {
      if (editingUser) {
        await updateAuthorisedUser(editingUser._id, formData);
        showSuccess('Authorised user updated successfully!');
      } else {
        await createAuthorisedUser(formData);
        showSuccess('Authorised user added successfully!');
      }

      const res = await fetchAuthorisedUsers();
      setUsers(res?.data || []);
      setTimeout(() => { setShowModal(false); resetForm(); }, 700);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleAuthorisedUser(id);
      const res = await fetchAuthorisedUsers();
      setUsers(res?.data || []);
      setListStatus({ type: 'success', message: 'Status updated.' });
      setTimeout(() => setListStatus(null), 2500);
    } catch (err) {
      setListStatus({ type: 'error', message: 'Toggle failed: ' + err.message });
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      authorisedUserCode: user.authorisedUserCode,
      name: user.name,
      isActive: user.isActive
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ authorisedUserCode: '', name: '', isActive: true });
    setEditingUser(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
          <div className="text-left">
            <h1 className="text-xl lg:text-3xl font-bold text-cyan-200 flex items-center gap-3">
              <FontAwesomeIcon icon={faUser} /> Authorised Users
            </h1>
            <p className="text-xs lg:text-sm text-gray-400">Manage users who can access this MiniERP</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-cyan-600 hover:bg-cyan-500 w-fit px-2 py-1 rounded-2xl flex items-center gap-2 font-medium"
          >
            <FontAwesomeIcon icon={faPlus} /> Add New User
          </button>
        </div>

        {/* Search */}
        <div className="bg-gray-800 rounded-2xl p-4 mb-6">
          <div className="relative w-full sm:max-w-md">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-3.5 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or user code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 pl-11 py-3 rounded-xl text-gray-200"
            />
          </div>
        </div>

        {listStatus && <div className="mb-4"><StatusBanner status={listStatus} /></div>}

        {/* Users List */}
        <div className="bg-gray-800 rounded-3xl overflow-hidden">
          {/* ================= MOBILE CARDS ================= */}
          <div className="block lg:hidden space-y-3 p-3">
            {filteredUsers.map((user) => (
              <div key={user._id} className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-xs">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono text-cyan-300">{user.authorisedUserCode}</div>
                    <div className="text-sm font-semibold text-white">{user.name}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggle(user._id)}
                      className="w-fit px-1 py-1 text-lg"
                    >
                      <FontAwesomeIcon
                        icon={user.isActive ? faToggleOn : faToggleOff}
                        className={user.isActive ? "text-emerald-400" : "text-gray-500"}
                      />
                    </button>
                    <button
                      onClick={() => openEditModal(user)}
                      className="w-fit px-1 py-1 text-lg text-gray-400 hover:text-cyan-400"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ================= DESKTOP TABLE ================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">User Code</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Name</th>
                  <th className="text-center py-4 px-6 text-gray-400 font-medium">Status</th>
                  <th className="text-center py-4 px-6 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="py-4 px-6 font-mono text-cyan-300">{user.authorisedUserCode}</td>
                    <td className="py-4 px-6 font-medium">{user.name}</td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => handleToggle(user._id)}
                        className="text-xl lg:text-3xl w-fit px-2 py-1 transition-colors"
                      >
                        <FontAwesomeIcon
                          icon={user.isActive ? faToggleOn : faToggleOff}
                          className={user.isActive ? "text-emerald-400" : "text-gray-500"}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-lg lg:text-xl w-fit px-2 py-1 text-gray-400 hover:text-cyan-400 transition-colors"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No authorised users found. Add your first user.
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-gray-500 text-sm">
          Note: These users can be used later for role-based access in MiniERP.
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 md:p-8">
            <h2 className="text-2xl font-bold text-cyan-200 mb-6 text-left">
              {editingUser ? 'Edit User' : 'Add Authorised User'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 mb-1">
                  User Code (think5whyscode) &mdash; {formData.authorisedUserCode.length}/{CODE_LENGTH}
                </label>
                <input
                  type="text"
                  value={formData.authorisedUserCode}
                  onChange={(e) => setFormData({ ...formData, authorisedUserCode: e.target.value.toUpperCase().slice(0, CODE_LENGTH) })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 font-mono"
                  placeholder="14 character code"
                  maxLength={CODE_LENGTH}
                  disabled={!!editingUser}
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3"
                  required
                />
              </div>

              <StatusBanner status={status} />

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium"
                >
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniERPAuthorisedUser;
